/**
 * App controller — the orchestration layer between the UI, the WebMCP tools
 * and the audio engine. Everything that is "an action a person or an agent can
 * take" goes through here so both surfaces share one implementation:
 *
 * - **Audio unlock** (SPEC §4.5): `engine.init()` must run inside a user
 *   gesture. The controller listens for the first document `pointerdown` /
 *   `keydown`, and `play()` before unlock does not start the transport — it
 *   spotlights the Play button and reports `audio: 'muted_until_user_taps'`.
 * - **Transport**: play / stop / toggle with human-action logging (only for
 *   `by: 'human'`; the `transport` tool passes `by: 'agent'`) and a
 *   start/stop notification (`onTransportChange`, `bm:transport`) so the Play
 *   button and bar tabs never go stale.
 * - **Live-write recording** (SPEC §2, §4.5): notes are buffered as they are
 *   played, mirrored into the store's transient layer so the grid and the
 *   engine show/play them immediately, and committed as ONE undo entry on
 *   `stopRecording()`.
 * - **Chord strip edits**: `setChordAt()` parses a symbol, re-voices the whole
 *   progression and rewrites the chords track through `setProgression`.
 * - **Shared buttons**: the header Download-MIDI button and the transport Play
 *   button are created here so tools (`export_midi`, `transport`) can target
 *   them without knowing the DOM layout.
 *
 * The engine is consumed through the structural `Engine` interface below so
 * this module never imports `audio/engine.ts` (built concurrently).
 */
import type { Store, NoteInput } from '../state/store-api';
import type { AppMode, Author, ChordRhythm, ChordSlot, DrumLane, Key, MelodicTrackId, Session, StyleId } from '../types';
import { STEPS_PER_BAR, TOTAL_STEPS, clamp } from '../types';
import { noteLengthFromSteps, snapStep } from '../music/quantize';
import { askHuman as defaultAsk, type AskOutcome, type AskParams } from '../webmcp/ask';
import { spotlight as defaultSpotlight } from '../webmcp/spotlight';
import { h } from '../ui/dom';

/** Public surface of `audio/engine.ts` as consumed by the controller and UI. */
export interface Engine {
  /** Idempotent; MUST be called inside a user-gesture handler. */
  init(): Promise<void>;
  /** True once the AudioContext is running and the rig is built. */
  ready: boolean;
  play(fromBar?: number): void;
  stop(): void;
  isPlaying(): boolean;
  position(): { bar: number; step: number };
  /** Transport position in fractional steps, 0..64. */
  nowStep(): number;
  /** Called on every 16th with the absolute step; returns an unsubscribe. */
  onStep(fn: (step: number) => void): () => void;
  previewNote(track: MelodicTrackId, pitch: number, velocity?: number): void;
  previewDrum(lane: DrumLane, velocity?: number): void;
  /** Play `bars` bars of metronome, then start the loop at step 0. */
  countIn(bars?: number): Promise<void>;
  loopSeconds(): number;
  barSeconds(): number;
  dispose(): void;
}

/** What the controller needs from `music/theory.ts` (structural; the real module satisfies it). */
export interface TheoryAdapter {
  /** Parse a numeral or chord name in `key`; returns a chord-like object, an `{ error }`, `null`, or throws. */
  parseChordSymbol(symbol: string, key: Key): unknown;
  /** Voice a progression (closest-voicing search); one voicing per input chord, in order. */
  voice(chords: unknown[], opts?: unknown): unknown;
  /** Articulate voiced slots into chord-track notes for a rhythm. */
  chordRhythmNotes(voicings: unknown, rhythm: ChordRhythm): unknown;
}

/** Who triggered a transport action; only `'human'` actions are logged to `human.recent`. */
export interface ActionOpts {
  by?: Author;
}

/** Result of `play()`: mirrors the `transport` tool's `audio` field. */
export interface PlayResult {
  playing: boolean;
  audio: 'ready' | 'muted_until_user_taps';
}

/** What `stopRecording()` reports (mirrors the `stop_recording` tool result). */
export interface RecordingSummary {
  notes: number;
  /** 1-based bars that received at least one note. */
  bars_touched: number[];
}

/** Outcome of a chord-strip edit: the committed slot, or a human-readable parse/voicing error. */
export type SetChordResult = { ok: true; slot: ChordSlot } | { ok: false; error: string };

/** Dependencies for `createController()`; everything beyond `store` and `engine` is optional and defaulted. */
export interface ControllerDeps {
  store: Store;
  engine: Engine;
  /** Chord parsing / voicing; `setChordAt` reports an error when absent. */
  theory?: TheoryAdapter;
  /** Confirmation dialog (defaults to `webmcp/ask.askHuman`). */
  ask?: (params: AskParams) => Promise<AskOutcome>;
  /** Persistence for `newSession`; lazily imported from `state/storage` when absent. */
  storage?: { save(s: Session): boolean };
  /** Presets for `newSession`; lazily imported from `music/presets` when absent. */
  presets?: { createSession(kind: 'demo' | 'blank'): Session };
  spotlight?: typeof defaultSpotlight;
  /** Document to attach the unlock gestures to (defaults to `document`). */
  doc?: Document;
}

/** The orchestration API shared by the UI and the WebMCP tools. */
export interface Controller {
  /** Calls `engine.init()`; safe to call repeatedly. Resolves when the engine reports ready (or init settles). */
  unlockAudio(): Promise<void>;
  audioReady(): boolean;
  /** Subscribe to the audio-ready transition; fires immediately if already ready. */
  onAudioReady(fn: () => void): () => void;
  /**
   * Start the loop (optionally from a 0-based bar). Before unlock: no-op + spotlight, `muted_until_user_taps`.
   * `opts.by` (default `'human'`) decides whether the action is logged to `human.recent`; tools pass `'agent'`.
   */
  play(fromBar?: number, opts?: ActionOpts): PlayResult;
  /** Stop the transport; also cancels/commits an in-progress recording. Logged only when something was running. */
  stop(opts?: ActionOpts): void;
  togglePlay(): void;
  /**
   * Subscribe to transport start/stop as seen by the controller (play, stop, count-in);
   * fires immediately with the current state. Optional on the interface so test doubles
   * built from the original contract stay valid; `createController()` always provides it.
   */
  onTransportChange?(fn: (playing: boolean) => void): () => void;
  /** Arm live-write recording on the lead track (count-in when the transport is stopped). */
  startRecording(): Promise<void>;
  /** Commit the recorded buffer as ONE undo entry and return to idle. */
  stopRecording(): RecordingSummary;
  isRecording(): boolean;
  /** Piano / computer-key note on: always previews on lead; records while armed. Velocity 0..1. */
  noteOn(pitch: number, velocity?: number): void;
  noteOff(pitch: number): void;
  /** Subscribe to app-mode changes (`idle` / `recording`). */
  onModeChange(fn: (mode: AppMode) => void): () => void;
  /**
   * Subscribe to every `stop()` — human, agent or Escape — whether or not the
   * transport was running. The app dismisses an open audition card from here,
   * so `audition` resolves `{chosen:null, reason:'stopped'}` even when audio
   * was never unlocked. Optional so test doubles built from the original
   * contract stay valid; `createController()` always provides it.
   */
  onStop?(fn: () => void): () => void;
  /** True once a recording is armed (count-in over, key presses are captured). Optional for test doubles. */
  isArmed?(): boolean;
  /** Confirm, then replace the session with the blank preset and save. */
  newSession(): Promise<void>;
  /** Parse `symbol` for 0-based `bar`, re-voice the progression, rewrite the chords track (by 'human'). */
  setChordAt(bar: number, symbol: string): SetChordResult;
  /** Set the Download-MIDI target (`href` + filename) and enable the button. */
  setDownload(href: string, filename: string): void;
  /** The header Download MIDI button (disabled until an `href` attribute is set). */
  downloadButton(): HTMLButtonElement;
  /** The transport Play button. */
  playButton(): HTMLButtonElement;
  /** Remove document listeners. */
  dispose(): void;
}

/** Default chord rhythm per style (SPEC §4.3). */
export const STYLE_DEFAULT_RHYTHM: Readonly<Record<StyleId, ChordRhythm>> = {
  lofi: 'pad',
  house: 'offbeat',
  synthwave: 'pulse',
};

/**
 * Keydown → transport latency compensation, seconds (SPEC §4.5). It equals the
 * engine's context lookAhead: `engine.nowStep()` reports the *scheduling*
 * position (`Tone.now()` = currentTime + lookAhead), 20 ms ahead of what the
 * human is hearing. The controller subtracts it exactly once, in
 * `audibleStep()`, and every recording timestamp (note-on, note-off, the
 * held-note flush on stop) goes through that one helper.
 */
export const RECORD_COMPENSATION_S = 0.02;

/** DOM event fired on `document` whenever the app mode changes (`detail: { mode }`). */
export const MODE_CHANGE_EVENT = 'bm:modechange';

/** DOM event fired on `document` whenever the controller starts or stops the transport (`detail: { playing }`). */
export const TRANSPORT_CHANGE_EVENT = 'bm:transport';

interface BufferedNote {
  step: number;
  pitch: number;
  length: number;
  velocity: number;
}

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  if (e && typeof e === 'object' && 'message' in e) return String((e as { message: unknown }).message);
  return String(e);
}

interface ParsedChord {
  numeral: string;
  name: string;
  borrowed: boolean;
  altered: boolean;
  pitches: number[];
  raw: unknown;
}

/** Coerce whatever `parseChordSymbol` returned into a `ParsedChord` or an error string. */
function normalizeParsed(result: unknown, symbol: string): ParsedChord | { error: string } {
  if (result == null) return { error: `unrecognised chord "${symbol}"` };
  if (typeof result !== 'object') return { error: `unrecognised chord "${symbol}"` };
  const r = result as Record<string, unknown>;
  if ('error' in r && r.error) {
    const err = r.error as unknown;
    return { error: typeof err === 'string' ? err : errorMessage(err) };
  }
  if ('ok' in r && r.ok === false) return { error: errorMessage(r.message ?? r.error ?? `unrecognised chord "${symbol}"`) };
  const chord = (r.chord && typeof r.chord === 'object' ? (r.chord as Record<string, unknown>) : r);
  const numeral = typeof chord.numeral === 'string' ? chord.numeral : symbol;
  const name = typeof chord.name === 'string' ? chord.name : typeof chord.symbol === 'string' ? chord.symbol : symbol;
  const pitches = Array.isArray(chord.pitches) ? (chord.pitches as unknown[]).filter((p): p is number => typeof p === 'number') : [];
  return {
    numeral,
    name,
    borrowed: chord.borrowed === true,
    altered: chord.altered === true,
    pitches,
    raw: result,
  };
}

/** Extract MIDI pitches from one voicing result (number[] or `{ pitches }`). */
function pitchesOf(v: unknown): number[] | null {
  if (Array.isArray(v) && v.every((x) => typeof x === 'number')) return v as number[];
  if (v && typeof v === 'object' && Array.isArray((v as { pitches?: unknown }).pitches)) {
    return ((v as { pitches: unknown[] }).pitches).filter((x): x is number => typeof x === 'number');
  }
  return null;
}

/** Coerce rhythm output into `NoteInput`s (strips any `by`; the store assigns it from meta). */
function toNoteInputs(v: unknown): NoteInput[] {
  if (!Array.isArray(v)) return [];
  const out: NoteInput[] = [];
  for (const n of v) {
    if (!n || typeof n !== 'object') continue;
    const o = n as Record<string, unknown>;
    if (typeof o.step !== 'number' || typeof o.pitch !== 'number') continue;
    out.push({
      step: o.step,
      pitch: o.pitch,
      length: typeof o.length === 'number' ? Math.max(1, o.length) : 1,
      velocity: typeof o.velocity === 'number' ? clamp(o.velocity, 0, 1) : 0.8,
    });
  }
  return out;
}

function userGestureActive(): boolean {
  const ua = (navigator as Navigator & { userActivation?: { isActive?: boolean } }).userActivation;
  return ua?.isActive === true;
}

/** Create the app controller. See the module header for responsibilities. */
export function createController(deps: ControllerDeps): Controller {
  const { store, engine } = deps;
  const ask = deps.ask ?? defaultAsk;
  const spot = deps.spotlight ?? defaultSpotlight;
  const doc = deps.doc ?? document;

  // ---------- shared buttons ----------
  const playBtn = h(
    'button',
    { type: 'button', class: 'bm-btn bm-btn--primary bm-transport__play', 'aria-label': 'Play', 'aria-keyshortcuts': 'Space' },
    h('span', { 'aria-hidden': 'true' }, '▶'),
    ' Play',
  );
  const downloadBtn = h(
    'button',
    { type: 'button', class: 'bm-btn bm-header__download', disabled: true, title: 'Ask your agent to export MIDI, then click to download' },
    h('span', { 'aria-hidden': 'true' }, '⤓'),
    ' Download MIDI',
  );
  const syncDownload = () => {
    downloadBtn.disabled = !downloadBtn.getAttribute('href');
  };
  const observer = typeof MutationObserver === 'function' ? new MutationObserver(syncDownload) : null;
  observer?.observe(downloadBtn, { attributes: true, attributeFilter: ['href', 'download'] });
  downloadBtn.addEventListener('click', () => {
    const href = downloadBtn.getAttribute('href');
    if (!href) return;
    const a = h('a', { href, download: downloadBtn.getAttribute('download') || 'bandmate.mid', hidden: true });
    doc.body.appendChild(a);
    a.click();
    a.remove();
  });

  // ---------- audio unlock ----------
  const readyListeners = new Set<() => void>();
  let readyAnnounced = false;
  let pendingPlay: { fromBar?: number; by: Author } | null = null;
  let disposed = false;
  /** True while the Play button's own click handler runs (no self-spotlight). */
  let playGesture = false;

  // ---------- transport notifications ----------
  const transportListeners = new Set<(playing: boolean) => void>();
  const stopListeners = new Set<() => void>();
  let lastPlaying = false;
  const notifyTransport = () => {
    const playing = engine.isPlaying();
    if (playing === lastPlaying) return;
    lastPlaying = playing;
    for (const l of transportListeners) l(playing);
    doc.dispatchEvent(new CustomEvent(TRANSPORT_CHANGE_EVENT, { detail: { playing } }));
  };

  const startPlayback = (fromBar?: number, by: Author = 'human') => {
    engine.play(fromBar);
    if (by === 'human') store.logHuman({ kind: 'play', detail: fromBar === undefined ? 'played' : `played from bar ${fromBar + 1}` });
    notifyTransport();
  };

  const afterReady = () => {
    if (readyAnnounced) return;
    readyAnnounced = true;
    removeGestureListeners();
    for (const l of readyListeners) l();
    if (pendingPlay) {
      const p = pendingPlay;
      pendingPlay = null;
      startPlayback(p.fromBar, p.by);
    }
  };

  const unlockAudio = async (): Promise<void> => {
    if (engine.ready) {
      afterReady();
      return;
    }
    try {
      await engine.init();
    } catch (e) {
      console.warn('[bandmate] audio init failed', e);
      return;
    }
    if (engine.ready) afterReady();
  };

  const onGesture = () => {
    if (!engine.ready) {
      void unlockAudio();
      return;
    }
    // Already initialised (listeners re-armed by a visibility change): a repeated
    // init() inside a fresh gesture re-resumes a context the browser suspended.
    removeGestureListeners();
    afterReady();
    engine.init().catch((e) => console.warn('[bandmate] audio resume failed', e));
  };
  const onVisibility = () => {
    // Coming back to the tab: the next gesture re-runs init() so the engine can resume its context.
    if (doc.visibilityState === 'visible' && !disposed) addGestureListeners();
  };
  let gestureListening = false;
  function addGestureListeners() {
    if (gestureListening) return;
    gestureListening = true;
    doc.addEventListener('pointerdown', onGesture, { capture: true, passive: true });
    doc.addEventListener('keydown', onGesture, { capture: true, passive: true });
  }
  function removeGestureListeners() {
    if (!gestureListening) return;
    gestureListening = false;
    doc.removeEventListener('pointerdown', onGesture, { capture: true });
    doc.removeEventListener('keydown', onGesture, { capture: true });
  }
  addGestureListeners();
  playBtn.addEventListener('click', () => {
    void unlockAudio();
    playGesture = true;
    try {
      controller.play();
    } finally {
      playGesture = false;
    }
  });
  doc.addEventListener('visibilitychange', onVisibility);

  // ---------- mode ----------
  const modeListeners = new Set<(m: AppMode) => void>();
  const setMode = (m: AppMode) => {
    store.setMode(m);
    for (const l of modeListeners) l(m);
    doc.dispatchEvent(new CustomEvent(MODE_CHANGE_EVENT, { detail: { mode: m } }));
  };

  // ---------- recording ----------
  let recording = false;
  let armed = false;
  let countingIn = false;
  let recToken = 0;
  let buffer: BufferedNote[] = [];
  const held = new Map<number, BufferedNote>();
  let unsubStore: (() => void) | null = null;

  const sixteenthSeconds = () => Math.max(1e-3, engine.barSeconds() / STEPS_PER_BAR);
  /** The loop position the human is hearing, in fractional steps 0..64 (see `RECORD_COMPENSATION_S`). */
  const audibleStep = () => {
    const s = engine.nowStep() - RECORD_COMPENSATION_S / sixteenthSeconds();
    return ((s % TOTAL_STEPS) + TOTAL_STEPS) % TOTAL_STEPS;
  };

  const mergedSession = (): Session => {
    const s = JSON.parse(JSON.stringify(store.get())) as Session;
    const lead = s.tracks.lead;
    for (const b of buffer) {
      lead.notes = lead.notes.filter((n) => !(n.step === b.step && n.pitch === b.pitch));
      lead.notes.push({ step: b.step, pitch: b.pitch, length: b.length, velocity: b.velocity, by: 'human' });
    }
    lead.notes.sort((a, b) => a.step - b.step || a.pitch - b.pitch);
    return s;
  };
  const pushTransient = () => {
    if (!armed) return;
    store.setTransient(mergedSession());
  };

  const arm = () => {
    armed = true;
    unsubStore = store.subscribe((_s, meta) => {
      if (meta.source !== 'transient' && armed) pushTransient();
    });
  };

  const disarm = () => {
    armed = false;
    recording = false;
    countingIn = false;
    unsubStore?.();
    unsubStore = null;
  };

  /** Abandon a recording that could not start (audio locked, count-in failed) without committing anything. */
  const abortRecording = (why: string) => {
    console.warn(`[bandmate] recording aborted: ${why}`);
    disarm();
    buffer = [];
    held.clear();
    if (store.mode() === 'recording') setMode('idle');
  };

  const startRecording = async (): Promise<void> => {
    if (recording) return;
    recording = true;
    armed = false;
    buffer = [];
    held.clear();
    const token = ++recToken;
    setMode('recording');
    if (!engine.isPlaying()) {
      // Record is a gesture: init() must be kicked synchronously here. unlockAudio()
      // shares the engine's in-flight promise, so awaiting it never double-inits.
      const unlock = unlockAudio();
      if (!engine.ready) await unlock;
      if (token !== recToken || !recording) return;
      if (!engine.ready) {
        abortRecording('audio is not unlocked');
        return;
      }
      countingIn = true;
      try {
        await engine.countIn(1);
      } catch (e) {
        if (token === recToken && recording) abortRecording(`count-in failed: ${errorMessage(e)}`);
        return;
      } finally {
        if (token === recToken) countingIn = false;
      }
      if (token !== recToken || !recording) return;
      notifyTransport();
    }
    arm();
  };

  const stopRecording = (): RecordingSummary => {
    if (!recording) return { notes: 0, bars_touched: [] };
    recToken++;
    // Cancelled during the count-in: nothing was armed, so stop the clicks
    // instead of letting the loop start behind an idle Record button.
    if (countingIn && !armed) {
      engine.stop();
      notifyTransport();
    }
    const nowStep = audibleStep();
    for (const entry of held.values()) entry.length = noteLengthFromSteps(entry.step, nowStep);
    held.clear();
    const notes: NoteInput[] = buffer.map((b) => ({ step: b.step, pitch: b.pitch, length: b.length, velocity: b.velocity }));
    buffer = [];
    disarm();
    if (store.hasTransient()) store.setTransient(null);
    const label = `recorded ${notes.length} note${notes.length === 1 ? '' : 's'} on lead`;
    if (notes.length) {
      store.apply({ type: 'writeNotes', track: 'lead', notes, mode: 'merge' }, { by: 'human', label });
      store.setLastRecording({ notes: notes.length, at: Date.now() });
      store.logHuman({ kind: 'record', detail: label });
    }
    setMode('idle');
    const bars = [...new Set(notes.map((n) => Math.floor(n.step / STEPS_PER_BAR) + 1))].sort((a, b) => a - b);
    return { notes: notes.length, bars_touched: bars };
  };

  const noteOn = (pitch: number, velocity = 0.8) => {
    const vel = clamp(velocity, 0, 1);
    engine.previewNote('lead', pitch, vel);
    if (!armed || held.has(pitch)) return;
    const step = snapStep(audibleStep());
    const entry: BufferedNote = { step, pitch, length: 1, velocity: vel };
    buffer = buffer.filter((b) => !(b.step === step && b.pitch === pitch));
    buffer.push(entry);
    held.set(pitch, entry);
    pushTransient();
  };

  const noteOff = (pitch: number) => {
    const entry = held.get(pitch);
    if (!entry) return;
    held.delete(pitch);
    entry.length = noteLengthFromSteps(entry.step, audibleStep());
    pushTransient();
  };

  // ---------- chords ----------
  /** Re-parse a stored slot into the parser's own shape so `voice()` can re-voice the whole progression. */
  const reparse = (theory: TheoryAdapter, slot: ChordSlot, key: Key): ParsedChord | null => {
    for (const text of [slot.numeral, slot.name]) {
      try {
        const r = normalizeParsed(theory.parseChordSymbol(text, key), text);
        if (!('error' in r)) return r;
      } catch {
        /* try the next spelling */
      }
    }
    return null;
  };

  const setChordAt = (bar: number, symbol: string): SetChordResult => {
    if (!Number.isInteger(bar) || bar < 0 || bar > 3) return { ok: false, error: 'bar must be 0..3' };
    const text = symbol.trim();
    if (!text) return { ok: false, error: 'type a numeral (i, VI, V7) or a chord name (Am, Fmaj7)' };
    const theory = deps.theory;
    if (!theory) return { ok: false, error: 'chord parsing is unavailable' };
    const s = store.get();
    let parsed: ParsedChord | { error: string };
    try {
      parsed = normalizeParsed(theory.parseChordSymbol(text, s.key), text);
    } catch (e) {
      return { ok: false, error: errorMessage(e) };
    }
    if ('error' in parsed) return { ok: false, error: parsed.error };

    // One entry per bar: the edited chord, the existing slot (re-parsed for voicing), or nothing.
    const perBar: Array<{ slot: ChordSlot; parsed: ParsedChord | null } | null> = [null, null, null, null];
    for (const x of s.progression ?? []) {
      if (x.bar < 0 || x.bar > 3 || x.bar === bar) continue;
      const slot: ChordSlot = { ...x, pitches: [...x.pitches] };
      perBar[x.bar] = { slot, parsed: reparse(theory, slot, s.key) };
    }
    perBar[bar] = {
      slot: { bar, numeral: parsed.numeral, name: parsed.name, pitches: parsed.pitches, borrowed: parsed.borrowed, altered: parsed.altered },
      parsed,
    };

    const rhythm: ChordRhythm = s.progressionRhythm ?? STYLE_DEFAULT_RHYTHM[s.style];
    const slots: ChordSlot[] = [];
    const notes: NoteInput[] = [];
    try {
      const toVoice = perBar.filter((x): x is { slot: ChordSlot; parsed: ParsedChord } => !!x && x.parsed !== null);
      const voiced = theory.voice(toVoice.map((x) => x.parsed.raw));
      const list = Array.isArray(voiced) ? voiced : [];
      toVoice.forEach((x, i) => {
        const pitches = pitchesOf(list[i]);
        if (pitches && pitches.length) x.slot.pitches = pitches;
      });
      for (const entry of perBar) {
        if (!entry) continue;
        const { slot } = entry;
        if (!slot.pitches.length) {
          if (slot.bar === bar) return { ok: false, error: `could not voice ${slot.name}` };
          continue;
        }
        slots.push(slot);
        const lo = slot.bar * STEPS_PER_BAR;
        const hi = lo + STEPS_PER_BAR;
        for (const n of toNoteInputs(theory.chordRhythmNotes([slot.pitches], rhythm))) if (n.step >= lo && n.step < hi) notes.push(n);
      }
    } catch (e) {
      return { ok: false, error: errorMessage(e) };
    }
    const slot = slots.find((x) => x.bar === bar)!;
    const label = `set bar ${bar + 1} chord ${slot.name}`;
    store.apply({ type: 'setProgression', slots, rhythm, notes }, { by: 'human', label });
    store.logHuman({ kind: 'set_chord', detail: label });
    return { ok: true, slot };
  };

  // ---------- new session ----------
  const newSession = async (): Promise<void> => {
    const outcome = await ask({
      question: 'Start a new session?',
      detail: 'This clears every track and the undo history. The current session is autosaved until then.',
      options: [
        { id: 'new', label: 'New session', description: 'Blank loop in the current key and style.' },
        { id: 'cancel', label: 'Keep this one' },
      ],
      dismissible: true,
    });
    if (outcome !== 'new') return;
    controller.stop();
    const presets = deps.presets ?? (await import('../music/presets'));
    const storage = deps.storage ?? (await import('../state/storage'));
    store.load(presets.createSession('blank'));
    try {
      storage.save(store.get());
    } catch (e) {
      console.warn('[bandmate] save failed', e);
    }
    store.logHuman({ kind: 'new_session', detail: 'started a new session' });
  };

  const controller: Controller = {
    unlockAudio,
    audioReady: () => engine.ready,
    onAudioReady(fn) {
      readyListeners.add(fn);
      if (engine.ready) fn();
      return () => readyListeners.delete(fn);
    },
    play(fromBar, opts) {
      const by: Author = opts?.by ?? 'human';
      if (!engine.ready) {
        pendingPlay = { fromBar, by };
        if (userGestureActive()) void unlockAudio();
        // The Play button's own click is already the unlocking gesture: no need to point at it.
        if (!playGesture) spot(playBtn, { label: 'press Play to unlock sound', scroll: false });
        return { playing: false, audio: 'muted_until_user_taps' };
      }
      pendingPlay = null;
      startPlayback(fromBar, by);
      return { playing: true, audio: 'ready' };
    },
    stop(opts) {
      const by: Author = opts?.by ?? 'human';
      const wasActive = engine.isPlaying() || recording || pendingPlay !== null;
      pendingPlay = null;
      if (recording) stopRecording();
      engine.stop();
      if (wasActive && by === 'human') store.logHuman({ kind: 'stop', detail: 'stopped' });
      notifyTransport();
      for (const l of stopListeners) l();
    },
    onStop(fn) {
      stopListeners.add(fn);
      return () => stopListeners.delete(fn);
    },
    togglePlay() {
      if (engine.isPlaying()) controller.stop();
      else controller.play();
    },
    onTransportChange(fn) {
      transportListeners.add(fn);
      fn(engine.isPlaying());
      return () => transportListeners.delete(fn);
    },
    startRecording,
    stopRecording,
    isRecording: () => recording,
    isArmed: () => armed,
    noteOn,
    noteOff,
    onModeChange(fn) {
      modeListeners.add(fn);
      return () => modeListeners.delete(fn);
    },
    newSession,
    setChordAt,
    setDownload(href, filename) {
      downloadBtn.setAttribute('href', href);
      downloadBtn.setAttribute('download', filename);
      syncDownload();
    },
    downloadButton: () => downloadBtn,
    playButton: () => playBtn,
    dispose() {
      disposed = true;
      removeGestureListeners();
      doc.removeEventListener('visibilitychange', onVisibility);
      observer?.disconnect();
      unsubStore?.();
      modeListeners.clear();
      readyListeners.clear();
      transportListeners.clear();
      stopListeners.clear();
    },
  };
  return controller;
}
